import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Contact, Comment, ApiResponse, PaginatedResponse } from '@pf/shared';
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api';

export interface ContactsParams {
  page?: number;
  limit?: number;
  search?: string;
  source?: string;
  hasReporter?: boolean;
  tags?: string;
  pressListId?: string;
  clientId?: string;
  company?: string;
  outreachStatus?: 'pitched' | 'replied' | 'no_response' | 'bounced';
  sortBy?: 'name' | 'lastOutreach' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  staleDays?: number;
}

export interface CreateContactBody {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  title?: string;
  company?: string;
  reporterId?: string;
  imageUrl?: string;
  notes?: string;
  tags?: string[];
  clientId?: string | null;
}

export type UpdateContactBody = Partial<CreateContactBody>;

const CONTACTS_KEY = 'contacts';
const COMMENTS_KEY = 'comments';

export function useContacts(params: ContactsParams = {}) {
  return useQuery({
    queryKey: [CONTACTS_KEY, params],
    queryFn: () =>
      apiGet<PaginatedResponse<Contact>>('/contacts', {
        page: params.page ?? 1,
        limit: params.limit ?? 20,
        search: params.search,
        source: params.source,
        hasReporter: params.hasReporter,
        tags: params.tags,
        pressListId: params.pressListId,
        clientId: params.clientId,
        company: params.company,
        outreachStatus: params.outreachStatus,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        staleDays: params.staleDays,
      }),
  });
}

export function useContact(id: string | undefined) {
  return useQuery({
    queryKey: [CONTACTS_KEY, id],
    queryFn: () => apiGet<{ contact: Contact }>(`/contacts/${id}`),
    enabled: !!id,
  });
}

export function useCreateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateContactBody) =>
      apiPost<ApiResponse<Contact>>('/contacts', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONTACTS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['reporters'] });
    },
  });
}

export interface BatchImportResult {
  created: Contact[];
  alreadyExists: Array<{ reporterId: string; contactId: string }>;
}

export function useBatchImportReporters() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { reporterIds: string[]; pressListId?: string }) =>
      apiPost<BatchImportResult>('/contacts/batch', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONTACTS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['reporters'] });
      queryClient.invalidateQueries({ queryKey: ['press-lists'] });
    },
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateContactBody }) =>
      apiPut<{ contact: Contact }>(`/contacts/${id}`, body),
    onMutate: async ({ id, body }) => {
      await queryClient.cancelQueries({ queryKey: [CONTACTS_KEY, id] });
      const previous = queryClient.getQueryData<{ contact: Contact }>([
        CONTACTS_KEY,
        id,
      ]);
      if (previous?.contact) {
        queryClient.setQueryData([CONTACTS_KEY, id], {
          contact: { ...previous.contact, ...body },
        });
      }
      return { previous };
    },
    onError: (_err, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData([CONTACTS_KEY, variables.id], context.previous);
      }
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: [CONTACTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [CONTACTS_KEY, variables.id] });
    },
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/contacts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONTACTS_KEY] });
    },
  });
}

// ── Tag operations ────────────────────────────────────────────────────────────

export function useTagSuggestions() {
  return useQuery({
    queryKey: [CONTACTS_KEY, 'tags', 'suggestions'],
    queryFn: () =>
      apiGet<{ tags: Array<{ tag: string; count: number }> }>(
        '/contacts/tags/suggestions',
      ),
  });
}

export function useReplaceContactTags() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tags }: { id: string; tags: string[] }) =>
      apiPut<{ contact: Contact }>(`/contacts/${id}/tags`, { tags }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [CONTACTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [CONTACTS_KEY, variables.id] });
      queryClient.invalidateQueries({
        queryKey: [CONTACTS_KEY, 'tags', 'suggestions'],
      });
    },
  });
}

export function useBulkUpdateTags() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      contactIds,
      addTags,
      removeTags,
    }: {
      contactIds: string[];
      addTags: string[];
      removeTags: string[];
    }) =>
      apiPost<{ updated: number }>('/contacts/bulk/tags', {
        contactIds,
        addTags,
        removeTags,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONTACTS_KEY] });
      queryClient.invalidateQueries({
        queryKey: [CONTACTS_KEY, 'tags', 'suggestions'],
      });
    },
  });
}

// ── Reporter sync ─────────────────────────────────────────────────────────────

export interface SyncDiffEntry {
  field: string;
  from: string | null;
  to: string | null;
}

export interface SyncReporterResult {
  contact: Contact;
  diff: SyncDiffEntry[];
}

export function useSyncReporter(contactId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost<SyncReporterResult>(`/contacts/${contactId}/sync-reporter`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONTACTS_KEY, contactId] });
      queryClient.invalidateQueries({ queryKey: [CONTACTS_KEY] });
    },
  });
}

// ── Deduplication ─────────────────────────────────────────────────────────────

export interface ContactWithListCount extends Contact {
  pressListCount: number;
}

export interface DuplicateGroup {
  contacts: ContactWithListCount[];
}

export function useContactDuplicates() {
  return useQuery({
    queryKey: [CONTACTS_KEY, 'duplicates'],
    queryFn: () =>
      apiGet<{ groups: DuplicateGroup[] }>('/contacts/duplicates'),
    staleTime: 0,
  });
}

export function useMergeContacts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      primaryContactId,
      mergeContactIds,
    }: {
      primaryContactId: string;
      mergeContactIds: string[];
    }) =>
      apiPost<{ contact: Contact }>('/contacts/merge', {
        primaryContactId,
        mergeContactIds,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONTACTS_KEY] });
    },
  });
}

// ── Comments ──────────────────────────────────────────────────────────────────

export function useContactComments(contactId: string | undefined) {
  return useQuery({
    queryKey: [COMMENTS_KEY, 'contact', contactId],
    queryFn: () =>
      apiGet<{ comments: Comment[] }>(`/contacts/${contactId}/comments`),
    enabled: !!contactId,
  });
}

export function useAddComment(contactId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (text: string) =>
      apiPost<{ comment: Comment }>(`/contacts/${contactId}/comments`, {
        text,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMMENTS_KEY, 'contact', contactId],
      });
    },
  });
}

export function useDeleteComment(contactId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      apiDelete(`/contacts/${contactId}/comments/${commentId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMMENTS_KEY, 'contact', contactId],
      });
    },
  });
}
